/**
 * useDeals — список последних сделок воронки КОММА и карточка одной сделки
 * (Bitrix24, read-only). Для блока «Последние сделки» на Обзоре и страницы /deals/:id.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { type Period, periodQuery, periodKey } from '@/components/ui/PeriodPicker';

export type DealStatus = 'won' | 'lost' | 'active';

export interface DealListItem {
  id: string;
  title: string;
  stageId: string;
  stageName: string;
  status: DealStatus;
  opportunity: number;
  utmSource: string;
  utmCampaign: string;
  dateCreate: string;
}

export interface DealsResponse {
  configured: boolean;
  period?: { from: string; to: string };
  total?: number;
  deals?: DealListItem[];
}

export function useDeals(period: Period, limit = 20) {
  return useQuery<DealsResponse>({
    queryKey: ['crm', 'deals', periodKey(period), limit],
    queryFn: () => apiFetch<DealsResponse>(`/api/crm/deals?${periodQuery(period)}&limit=${limit}`),
    staleTime: 120_000,
    placeholderData: keepPreviousData,
  });
}

export interface DealContact {
  id: string;
  name: string;
  phone: string;
  email: string;
  post: string;
}

export interface DealDetail {
  id: string;
  title: string;
  stageId: string;
  stageName: string;
  status: DealStatus;
  opportunity: number;
  currency: string;
  probability: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  dateCreate: string;
  dateModify: string;
  beginDate: string;
  closeDate: string;
  comments: string;
  contact: DealContact | null;
}

export interface DealResponse {
  configured: boolean;
  deal?: DealDetail;
}

export function useDeal(id: string) {
  return useQuery<DealResponse>({
    queryKey: ['crm', 'deal', id],
    queryFn: () => apiFetch<DealResponse>(`/api/crm/deals/${id}`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}
